/*
 * Why there is nothing to review, when that is not a failure.
 *
 * `GET /project/mapping/review/:orgId` answers with a SmartError for five
 * situations that are entirely normal, and the client used to render all five
 * as the red "the proposed groupings could not be loaded" panel:
 *
 *   404 PROJECT_MAPPING_NOT_PLANNED       nobody has planned this organization
 *   409 PROJECT_MAPPING_PLAN_IN_PROGRESS  a plan is being built right now
 *   409 PROJECT_MAPPING_PLAN_FAILED       the last run did not finish
 *   409 PROJECT_MAPPING_PLAN_MISSING      the run has no plan body recorded
 *   409 PROJECT_MAPPING_PLAN_EXPIRED      the stored plan body has aged out
 *   403 PROJECT_MAPPING_REVIEW_FORBIDDEN  / _OUT_OF_SCOPE
 *
 * The first of those is the common case and was the damaging one. Until an
 * organization has been planned there is no run, so EVERY organization
 * answered 404 and every row on the dashboard led to a screen that said the
 * groupings "could not be loaded" -- reporting a broken system for a state
 * that is simply the beginning of the process. An operator cannot tell that
 * apart from an outage, and has no reason to believe the next click will work
 * any better.
 *
 * Each of these has a different next action, which is the whole reason they
 * are distinguished rather than collapsed:
 *
 *   NOT_PLANNED / FAILED / EXPIRED -> plan it (operator)
 *   PLANNING                       -> wait; the screen re-checks itself
 *   FORBIDDEN                      -> nothing; it is not this role's to see
 *
 * Transcribed from the server's `ProjectMapping.review` controller. The codes
 * arrive as `SmartError.message`, which is a stable branching token and NOT a
 * sentence to display -- `details` carries the server's English, which is
 * English in all 21 locales and so can only be shown as technical detail.
 *
 * Kept free of @angular imports so tests/ProjectMappingNotReady.smoke.ts can
 * assert it under plain ts-node.
 */
export type MappingNotReadyReason =
  'NOT_PLANNED' | 'PLANNING' | 'FAILED' | 'EXPIRED' | 'FORBIDDEN';

/**
 * Classify a server error code, or `null` when it is a genuine failure that
 * should be reported as one.
 *
 * Unknown codes deliberately answer `null`. A code this client has never
 * heard of is not something to reassure anybody about: the honest rendering
 * is the error panel plus the technical detail, which is what gets the
 * mismatch noticed and fixed.
 */
export function mappingNotReadyReason(code: string): MappingNotReadyReason | null {
  switch ( code ) {
    case 'PROJECT_MAPPING_NOT_PLANNED':         return 'NOT_PLANNED';
    case 'PROJECT_MAPPING_PLAN_IN_PROGRESS':    return 'PLANNING';
    case 'PROJECT_MAPPING_PLAN_FAILED':         return 'FAILED';
    case 'PROJECT_MAPPING_PLAN_MISSING':        return 'EXPIRED';
    case 'PROJECT_MAPPING_PLAN_EXPIRED':        return 'EXPIRED';
    case 'PROJECT_MAPPING_REVIEW_FORBIDDEN':    return 'FORBIDDEN';
    case 'PROJECT_MAPPING_REVIEW_OUT_OF_SCOPE': return 'FORBIDDEN';
    case 'PROJECT_MAPPING_REVIEW_BAD_ORG':      return null;
    default:                                    return null;
  }
}

/** True when an operator pressing Plan is the move that unblocks this. */
export function planningWouldHelp(reason: MappingNotReadyReason | null): boolean {
  return reason === 'NOT_PLANNED' || reason === 'FAILED' || reason === 'EXPIRED';
}
